import { Namespace, type KeysOf, type TFn } from "@racoon/i18n";
import { IcLocation } from "@racoon/icons";
import { ConnectProto } from "@racoon/api";
import { formatTimestampToDate, fromTimestamp, enumColors } from "@racoon/lib";
import { fmt } from "@racoon/ui";
import type { ReactNode } from "react";
import { getPickupStatusColor } from "~/shared/utils";
import { OrganizationDetails } from "../components/organization.details";
import { PickupLocationDetails } from "../components/pickup-location.details";
import { TruckDetails } from "../components/truck.details";
import { DepotDetails } from "../components/depot.details";
import { DisposalSiteDetails } from "../components/disposalSite.details";
import { SuspiciousSitesDetails } from "../components/suspicious-sites.details";
import type {
  RecordsListFilterDynamicOptions,
  RecordTypeRoute,
} from "./records-list-filter-config";
import { WasteExportDetails } from "../components/waste-export.details";

/** The two lists behind the "waste export" registry entry. */
export type WasteExportTab = "jobs" | "ai";

export type RecordsConfigItem = {
  id: RecordTypeRoute;
  titleKey: string;
  content: ReactNode;
  data: any[];
  cardTitle: (item: any) => ReactNode;
  cardContent: (item: any) => any[];
  pageTitle?: (item: any) => ReactNode;
  totalCount?: number;
};

type GetRecordsConfigArgs = {
  t: TFn;
  listFilterDynamic: RecordsListFilterDynamicOptions;
  pickupLocationsData: any;
  jobsData: any;
  serviceProvidersData: any;
  trucksData: any;
  depotsData: any;
  disposalSitesData: any;
  suspiciousLocationsData?: any;
  anomaliesData?: any;
  /** `{ aiVerifications: GovAiVerificationRow[], listInfo }` — the photo-recognition tab's list. */
  aiVerificationsData?: any;
  /**
   * Which tab of the waste-export registry is open: the jobs list, or the AI
   * photo-recognition list. Only this one entry has two data sources.
   */
  wasteExportTab?: WasteExportTab;
  selectedRecordId: string | undefined;
};

export function getRecordsConfig({
  t,
  listFilterDynamic,
  pickupLocationsData,
  jobsData,
  serviceProvidersData,
  trucksData,
  depotsData,
  disposalSitesData,
  suspiciousLocationsData,
  anomaliesData,
  aiVerificationsData,
  wasteExportTab = "jobs",
  selectedRecordId,
}: GetRecordsConfigArgs): RecordsConfigItem[] {
  const organizations = serviceProvidersData?.organizations ?? [];
  const organizationIds = new Set(organizations.map((org: any) => org.id));
  const isAiTab = wasteExportTab === "ai";

  return [
    {
      id: "service-providers",
      titleKey: "records.serviceProviders",
      content: (
        <OrganizationDetails
          districtOptions={listFilterDynamic.districtOptions}
          selectedRecordId={selectedRecordId}
        />
      ),
      data: [...organizations]
        .filter((item) => item.parentId == null || !organizationIds.has(item.parentId))
        .sort((a: any, b: any) => Number(b.numberOfTrucks) - Number(a.numberOfTrucks)),
      cardTitle: (org: any) => org.name,
      cardContent: (org: any) => [
        { label: t(Namespace.Govdash, "bin"), value: org.bin },
        { label: t(Namespace.Govdash, "autopark") + ":", value: org.numberOfTrucks },
      ],
    },
    {
      id: "waste-export",
      titleKey: "records.wasteExport",
      content: <WasteExportDetails selectedRecordId={selectedRecordId} />,
      data: isAiTab
        ? (aiVerificationsData?.aiVerifications ?? [])
            .filter((row: any) => row?.job)
            .map((row: any) => ({ ...row.job, aiVerification: row.aiVerification }))
        : (jobsData?.jobs ?? []),
      totalCount: isAiTab
        ? (aiVerificationsData?.listInfo?.totalItems ?? 0)
        : (jobsData?.listInfo?.totalItems ?? 0),
      cardTitle: (job: any) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0">
            <IcLocation
              className="w-6 h-6 z-10"
              style={{ color: getPickupStatusColor(job.status) }}
            />
          </div>
          <p
            className="text-base font-semibold text-start leading-4 tracking-tight text-slate-900 truncate"
            title={job.pickupLocation.name}
          >
            {job.pickupLocation.name}
          </p>
        </div>
      ),
      cardContent: (job: any) => {
        const jobInstant = fromTimestamp(
          job.factEndAt ?? job.factStartAt ?? job.planEndAt ?? job.planStartAt ?? job.updatedAt,
        );
        const statusKey =
          `JOB_STATUS_${ConnectProto.JobStatus[job.status]}` as KeysOf<Namespace.Enum>;
        const statusColor = enumColors[statusKey];
        return [
          {
            label: t(Namespace.Govdash, "serviceProvider"),
            value: job.serviceProvider.name,
          },
          {
            label: t(Namespace.Govdash, "wasteExport.exportStatus"),
            value: (
              <div className="flex items-center gap-2">
                {job.status !== ConnectProto.JobStatus.ASSIGNED && jobInstant && (
                  <span className="text-text-secondary">
                    {fmt.dateTime(jobInstant, "dd MMM yyyy HH:mm")}
                  </span>
                )}
                <div
                  className={`min-w-[110px] py-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs justify-center ${statusColor?.bg ?? ""} ${statusColor?.color ?? ""} ${statusColor?.borderColor ? `${statusColor.borderColor} border` : ""}`}
                >
                  {t(Namespace.Enum, statusKey)}
                </div>
              </div>
            ),
          },
          ...(job.status === ConnectProto.JobStatus.NOT_COMPLETED
            ? [
                {
                  label: t(Namespace.Govdash, "wasteExport.reason"),
                  value: (
                    <span className="text-brand-red truncate">
                      {job?.driverComment?.split("=")?.[1]?.split(":")?.[0] ??
                        t(Namespace.Govdash, "wasteExport.reasonNotSpecified")}
                    </span>
                  ),
                },
              ]
            : []),
        ];
      },
    },
    {
      id: "pickup-locations",
      titleKey: "records.pickupLocations",
      content: <PickupLocationDetails selectedRecordId={selectedRecordId} />,
      data: pickupLocationsData?.pickupLocations ?? [],
      cardTitle: (pickupLocation: any) => (
        <div className="flex items-center gap-2">
          <div>
            <IcLocation className="w-6 h-6 text-brand-blue z-10 " />
          </div>
          <p className="text-base font-semibold text-start leading-4 tracking-tight text-slate-900">
            {pickupLocation.name}
          </p>
        </div>
      ),
      cardContent: (pickupLocation: any) => [
        // {
        //   label: t(Namespace.Govdash, "wasteGenerator"),
        //   value: pickupLocation.serviceProviderId,
        // },
        {
          label: t(Namespace.Govdash, "status.responsibleMvo"),
          value: pickupLocation.serviceProviderName,
        },
      ],
    },
    {
      id: "trucks",
      titleKey: "records.trucks",
      content: <TruckDetails selectedRecordId={selectedRecordId} />,
      data: (trucksData?.trucks ?? []).filter((truck: any) => truck?.lat && truck?.lon),
      cardTitle: ({ truck }: any) => (
        <div className="flex items-center gap-2">
          <p className="text-base font-semibold text-start leading-4 tracking-tight text-slate-900 px-2 py-1 rounded-sm border border-slate-900">
            {truck?.plateNumber ?? "—"}
          </p>
          <p className="text-base font-normal text-text-secondary">{truck?.model}</p>
        </div>
      ),
      pageTitle: ({ truck }: any) => (
        <p className="text-base font-semibold text-start leading-4 tracking-tight text-slate-900 px-2 py-1 rounded-sm border border-slate-900">
          {truck?.plateNumber ?? "—"}
        </p>
      ),
      cardContent: ({ serviceProvider }: any) => [
        {
          label: t(Namespace.Govdash, "subtitle.serviceProvider"),
          value: serviceProvider?.name || "",
        },
        // {
        //   label: t(Namespace.Govdash, "status.onLine"),
        //   value: truck.directorFullName,
        // },
      ],
    },
    {
      id: "depots",
      titleKey: "records.depots",
      content: <DepotDetails selectedRecordId={selectedRecordId} />,
      data: depotsData?.depots ?? [],
      cardTitle: ({ depot }: any) => depot.name,
      cardContent: ({ ownerOrganization, numberOfTrucks }: any) => [
        {
          label: t(Namespace.Govdash, "subtitle.serviceProvider"),
          value: ownerOrganization?.name || "",
        },
        {
          label: t(Namespace.Govdash, "autopark"),
          value: `${numberOfTrucks || 0} ${t(Namespace.Govdash, "vehicles")}`,
        },
      ],
    },
    {
      id: "disposal-sites",
      titleKey: "records.disposalSites",
      content: <DisposalSiteDetails selectedRecordId={selectedRecordId} />,
      data: disposalSitesData?.organizations ?? [],
      cardTitle: (org: any) => org.name,
      cardContent: (org: any) => [{ label: t(Namespace.Govdash, "bin"), value: org.bin }],
    },
    {
      id: "suspicious-sites",
      titleKey: "records.suspiciousSites",
      content: <SuspiciousSitesDetails />,
      data: suspiciousLocationsData?.locations ?? [],
      cardTitle: (data: any) => data.location.name,
      cardContent: (data: any) => {
        // Use anomaliesCount from analytics data if available, default to 0
        const anomaliesCount = data.totalStops ?? 0;
        const textColor = anomaliesCount === 0 ? "text-brand-green" : "text-brand-red";
        const rows = [
          {
            label: t(Namespace.Govdash, "anomaliesDetectedInGivenPeriod"),
            value: <span className={textColor}>{anomaliesCount}</span>,
          },
        ];
        if (data.archivedAt?.seconds != null) {
          rows.push({
            label: t(Namespace.Govdash, "suspiciousSites.archivedAt"),
            value: (
              <span className="text-text-secondary">{formatTimestampToDate(data.archivedAt)}</span>
            ),
          });
        }
        return rows;
      },
    },
    {
      id: "anomalies",
      titleKey: "records.anomalies",
      content: null,
      data: anomaliesData?.gpsStops ?? [],
      cardTitle: (anomaly: any) => (
        <div className="flex items-center gap-2">
          <div className="border border-text-primary rounded px-2 py-1 font-semibold uppercase text-sm">
            {anomaly?.truck?.plateNumber}
          </div>
          <div className="text-gray-500 text-sm truncate">{anomaly?.ownerOrganization?.name}</div>
        </div>
      ),
      cardContent: () => [
        {
          label: t(Namespace.Govdash, "anomalies.reason"),
          value: t(Namespace.Govdash, "anomalies.unauthorizedStop"),
        },
      ],
    },
  ];
}
